// The executable fast recipes ledger: one row per recipe version with its
// lifecycle state, run counters and last verification. Filterable by state;
// click a row to open the detail panel.

import type { RecipeRead } from '../../services/fastpathQuality';
import { whenUtc } from './format';
import styles from '../FastpathQualityPage.module.css';

export const RECIPE_STATES = ['draft', 'verified', 'active', 'disabled', 'superseded'] as const;

interface Props {
  recipes: RecipeRead[];
  total: number;
  state: string;
  selectedId: string | null;
  onStateChange: (state: string) => void;
  onSelect: (id: string) => void;
}

export function verdictLabel(r: RecipeRead): string {
  const v = r.verification?.verdict;
  if (!v) return '–';
  const streak = r.verification?.fail_streak ?? 0;
  return streak > 0 ? `${v} (${streak} failed)` : v;
}

export function RecipesTable({ recipes, total, state, selectedId, onStateChange, onSelect }: Props) {
  return (
    <section className={styles.panel} aria-label="Recipes">
      <div className={styles.panelHead}>
        <h2>Recipes</h2>
        <label className={styles.meta}>
          state{' '}
          <select value={state} onChange={(e) => onStateChange(e.target.value)} aria-label="Recipe state filter">
            <option value="">all</option>
            {RECIPE_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {' '}· <span data-testid="recipes-total">{`${total} recipe${total === 1 ? '' : 's'}`}</span> · click a row to open it
        </label>
      </div>
      {recipes.length === 0 ? (
        <p className={styles.empty}>No recipe matches. The analyst saves one through its save tool; seeds land with the runtime.</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>state</th>
                <th>slug / name</th>
                <th className={styles.num}>v</th>
                <th>lang</th>
                <th>entry</th>
                <th className={styles.num}>runs</th>
                <th className={styles.num}>fails</th>
                <th>last verified</th>
                <th>verdict</th>
              </tr>
            </thead>
            <tbody>
              {recipes.map((r) => (
                <tr
                  key={r.id}
                  className={`${styles.clickable} ${r.id === selectedId ? styles.selectedRow : ''}`}
                  onClick={() => onSelect(r.id)}
                  data-state={r.state}
                  data-recipe-id={r.id}
                >
                  <td><span className={`${styles.chip} ${styles[`recipe_${r.state}`] ?? ''}`}>{r.state}</span></td>
                  <td className={styles.wrap}>
                    <code className={styles.tag}>{r.slug}</code>
                    <div className={styles.note}>{r.name}</div>
                  </td>
                  <td className={styles.num}>{r.version}</td>
                  <td>{r.language}</td>
                  <td><code className={styles.tag} title={r.playbook_entry_id}>{r.playbook_entry_id.slice(0, 8)}</code></td>
                  <td className={styles.num}>{r.run_count.toLocaleString()}</td>
                  <td className={`${styles.num} ${r.consecutive_failures > 0 ? styles.flaggedCell : ''}`}>{r.consecutive_failures}</td>
                  <td>{whenUtc(r.last_verified_at)}</td>
                  <td className={styles.wrap} title={r.verification?.reason ?? undefined}>{verdictLabel(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
